<template>
    <div>
        <b-modal ref="caseDetailModal" id="case-detail" size="xl" :title="modalTitle" scrollable
            :visible="isModalVisible" no-fade static
            :hide-footer="!$permissions.some(p => ['compliance/edit cases', 'compliance/resolve cases', 'compliance/accept cases'].includes(p.permissionCodeName.toLowerCase()))"
            @hidden="hideModal" hide-header-close>
            <template #modal-header>
                <h5>{{ modalTitle }}</h5>
                <div class="d-flex">
                    <history-modal v-model="caseSelected" />
                    <button type="button" aria-label="Close" class="close" style="position: relative;top:0;right:0;"
                        @click="$bvModal.hide('case-detail')">×</button>
                </div>
            </template>
            <b-row>
                <b-col cols="4">
                    <b-row>
                        <b-col>
                            <card plain>
                                <b-row>
                                    <b-col>
                                        <h4 class="card-subtitle">
                                            {{ caseSelected?.lastName }},
                                            {{ caseSelected?.firstName }}
                                        </h4>
                                        <router-link v-if="caseSelected?.patientId"
                                            :to="{ name: 'PatientDashboard', params: { id: caseSelected.patientId } }">
                                            <span>Go To Profile</span> <i class="fas fa-user"></i>
                                        </router-link>
                                    </b-col>
                                </b-row>
                                <hr>
                                <b-row>
                                    <b-col>
                                        <label><strong>DOB: </strong></label>
                                        {{ caseSelected?.dateOfBirth | formatLocalDate('L') }}
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <label><strong>P: </strong></label>
                                        {{ caseSelected?.phoneNumber | formatPhone }}
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <label><strong>C: </strong></label>
                                        {{ caseSelected?.cellNumber | formatPhone }}
                                    </b-col>
                                </b-row>
                            </card>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <card plain>
                                <b-row>
                                    <b-col>
                                        <b-button v-if="isEditable && !editingType" class="pull-right" size="sm"
                                            variant="outline-primary" @click="editingType = true"
                                            style="margin-top: -5px;margin-right:-5px;" :disabled="disableFields">
                                            Edit Type
                                        </b-button>
                                        <label><strong>Type: </strong></label><br>
                                        <b-form-select v-if="editingType" v-model="selectedProblem" :options="problems"
                                            size="sm" />
                                        <template v-else>{{ caseSelected?.problem }}</template>
                                    </b-col>
                                </b-row>
                            </card>
                        </b-col>
                    </b-row>
                    <b-row v-if="categories.length > 0">
                        <b-col>
                            <card plain>
                                <b-row>
                                    <b-col>
                                        <b-button v-if="isEditable && !editingCategory" class="pull-right" size="sm"
                                            variant="outline-primary" @click="editingCategory = true"
                                            style="margin-top: -5px;margin-right:-5px;" :disabled="disableFields">
                                            Edit Category
                                        </b-button>
                                        <label><strong>Category: </strong></label><br>
                                        <b-form-select v-if="editingCategory" v-model="selectedCategory" :options="categoryOptions"
                                            size="sm" />
                                        <template v-else>{{ caseSelected?.categoryName || '(None)' }}</template>
                                    </b-col>
                                </b-row>
                            </card>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <card plain>
                                <b-row>
                                    <b-col>
                                        <b-button v-if="isEditable && !editingStore && !hasLinkedPrescriptions" class="pull-right" size="sm"
                                            variant="outline-primary" @click="editingStore = true"
                                            style="margin-top: -5px;margin-right:-5px;" :disabled="disableFields">
                                            Edit Store
                                        </b-button>
                                        <label><strong>Store: </strong></label><br>
                                        <SearchComponent v-if="editingStore" v-model="selectedStore"
                                            label=""
                                            search-u-r-l="/Store"
                                            :prepopulate="true"
                                            :clearable="true"
                                            :option-label="store => store.name" />
                                        <template v-else>{{ caseSelected?.storeName || '(None)' }}</template>
                                        <small v-if="hasLinkedPrescriptions" class="text-muted d-block">
                                            Store is determined by linked prescriptions
                                        </small>
                                    </b-col>
                                </b-row>
                            </card>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <card plain>
                                <b-row>
                                    <b-col>
                                        <b-button v-if="isEditable && !editingDoctor" class="pull-right" size="sm"
                                            variant="outline-primary" @click="editingDoctor = true"
                                            style="margin-top: -5px;margin-right:-5px;" :disabled="disableFields">
                                            Edit Doctor
                                        </b-button>
                                        <label><strong>Doctor: </strong></label><br>
                                        <SearchComponent v-if="editingDoctor" v-model="selectedDoctor"
                                            label=""
                                            search-u-r-l="/Prescriber"
                                            :prepopulate="true"
                                            :clearable="true"
                                            :option-label="prescriber => prescriber.lastName + ', ' + prescriber.firstName + (prescriber.npiNumber ? ' (NPI: ' + prescriber.npiNumber + ')' : '')" />
                                        <template v-else>{{ doctorDisplayName }}</template>
                                    </b-col>
                                </b-row>
                            </card>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <card plain>
                                <b-row>
                                    <b-col>
                                        <label><strong>Case Opened: </strong></label>
                                        {{ caseSelected?.created | formatDate("l LT", true) }}
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <label><strong>Case Opened By: </strong></label>
                                        {{ caseSelected?.createdBy }}
                                    </b-col>
                                </b-row>
                            </card>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <card plain>
                                <b-row class="mb-2">
                                    <b-col cols="12">
                                        <h5 class="card-subtitle">Shipments</h5>
                                    </b-col>
                                </b-row>
                                <b-table v-if="caseSelected?.id" id="shipments-table"
                                    :api-url="`/api/Case/${caseSelected.id}/Shipments`" :items="shipmentTableProvider"
                                    :fields="shipmentFields" show-empty outlined stacked>
                                    <template #table-busy>
                                        <div class="text-center my-2">
                                            <b-spinner class="align-middle" />
                                            <strong>Loading...</strong>
                                        </div>
                                    </template>
                                    <template #cell(trackingNumber)="data">
                                        <b-link :href="data.item.trackingUrl" target="_blank">{{ data.value }}</b-link>
                                    </template>
                                </b-table>
                            </card>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col cols="8">
                    <card plain>
                        <b-row>
                            <b-col cols="9">
                                <h5 class="card-subtitle">Scripts</h5>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <el-table :data="prescriptions">
                                    <el-table-column property="drugName" label="Drug">
                                    </el-table-column>
                                    <el-table-column property="rxNumber" label="Rx">
                                    </el-table-column>
                                    <el-table-column property="quantity" label="Qty">
                                    </el-table-column>
                                    <el-table-column label="Filled">
                                        <template slot-scope="props">
                                            {{ (props.row.rfNumber || 0) }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="Expires">
                                        <template slot-scope="props">
                                            {{ props.row.expirationDate | formatLocalDate('MM-DD-YYYY') }}
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </b-col>
                        </b-row>
                    </card>
                    <card plain>
                        <b-row>
                            <b-col cols="12">
                                <h5 class="card-subtitle">Notes</h5>
                            </b-col>
                            <b-col cols="12">
                                <NotesList ref="notesList" :entity-id="caseSelected?.id" :entity-type="caseType"
                                    :paging="false" />
                            </b-col>
                            <b-col cols="12" class="pt-2">
                                <NotesForm ref="noteForm" :entity-id="caseSelected?.id" :entity-type="caseType"
                                    @saved="refreshNotesList" @input="newNote = $event" />
                            </b-col>
                        </b-row>
                    </card>
                    <card plain>
                        <b-row class="mb-2">
                            <b-col cols="9">
                                <h5 class="card-subtitle">Documents</h5>
                            </b-col>
                            <b-col cols="3" class="text-right">
                                <b-button v-if="isEditable && caseSelected?.patientId && !disableFields"
                                    size="sm" variant="outline-primary"
                                    @click="showDocUpload = !showDocUpload">
                                    {{ showDocUpload ? 'Cancel' : 'Upload' }}
                                </b-button>
                            </b-col>
                        </b-row>
                        <b-row v-if="showDocUpload" class="mb-3">
                            <b-col>
                                <b-form @submit.prevent="uploadDocument">
                                    <b-form-group label="File (PDF)" label-size="sm">
                                        <b-form-file v-model="newDocFile" accept=".pdf"
                                            size="sm" placeholder="Choose a PDF file..." />
                                    </b-form-group>
                                    <b-form-group label="File Name" label-size="sm">
                                        <b-form-input v-model="newDocFileName" size="sm"
                                            placeholder="Document name" maxlength="50" />
                                    </b-form-group>
                                    <b-button type="submit" size="sm" variant="primary"
                                        :disabled="isUploadingDoc || !newDocFile || !newDocFileName">
                                        <b-spinner v-if="isUploadingDoc" small class="mr-1" />
                                        {{ isUploadingDoc ? 'Uploading...' : 'Save' }}
                                    </b-button>
                                </b-form>
                            </b-col>
                        </b-row>
                        <b-table :items="caseDocuments" :fields="documentFields"
                            show-empty outlined small stacked="sm"
                            empty-text="No documents linked to this case">
                            <template #cell(fileName)="data">
                                <b-link @click="viewDocument(data.item)">{{ data.value }}</b-link>
                            </template>
                            <template #cell(created)="data">
                                {{ data.value | formatDate('l LT', true) }}
                                <small v-if="data.item.createdBy" class="text-muted d-block">{{ data.item.createdBy }}</small>
                            </template>
                            <template #cell(actions)="data">
                                <b-button v-if="isEditable && !disableFields"
                                    size="sm" variant="outline-danger"
                                    @click="unlinkDocument(data.item)">
                                    <i class="fas fa-times"></i>
                                </b-button>
                            </template>
                        </b-table>
                    </card>
                    <card plain v-if="isEditable">
                        <CasesWatcherComponent
                            :case-id="caseSelected?.id"
                            :disabled="disableFields"
                            @input="onWatchersChange" />
                    </card>
                    <card plain v-if="isEditable">
                        <CaseAssignmentComponent
                            v-model="localCaseAssignment"
                            :disabled="disableFields"
                            label="Assigned To" />
                    </card>
                    <card plain v-if="isEditable && !disableFields">
                        <b-row>
                            <b-col>
                                <strong>Is the case resolved? </strong>
                            </b-col>
                        </b-row>
                        <div v-if="mode == 'opened'" class="d-flex" style="gap: 10px;">
                            <n-radio v-model="isResolved" label="true">YES</n-radio>
                            <n-radio v-model="isResolved" label="false">NO</n-radio>
                        </div>
                        <div v-if="mode == 'resolved'" class="d-flex" style="gap: 10px;">
                            <n-radio v-model="option" label="accept">Accept</n-radio>
                            <n-radio v-model="option" label="reject">Reject</n-radio>
                        </div>
                        <b-row>
                            <b-col>
                                <strong>Is this an error? </strong>
                            </b-col>
                        </b-row>
                        <div class="d-flex" style="gap: 10px;">
                            <n-radio v-model="carePointsError" label="true">YES</n-radio>
                            <n-radio v-model="carePointsError" label="false">NO</n-radio>
                        </div>
                    </card>
                    <card v-if="this.caseSelected?.questionSetId" plain>
                        <h5 class="card-subtitle mb-2">Questions</h5>

                        <TaskQuestions ref="questions" :questions="questions" :answers="answers"
                            :show-errors="showErrors" :disabled="mode !== 'opened'"
                            :entity-type="EntityType.Case" />
                    </card>
                </b-col>
            </b-row>
            <template #modal-footer="{ cancel }">
                <div class="d-flex w-100" style="gap: 8px;">
                    <b-button @click="cancel" variant="secondary">Cancel</b-button>
                    <div class="flex-fill"></div>
                    <b-button
                        v-if="mode === 'opened'"
                        @click="handleSavePartialAnswers"
                        variant="info"
                        :disabled="savingPartialAnswers">
                        <b-spinner v-if="savingPartialAnswers" small class="mr-1" />
                        {{ savingPartialAnswers ? 'Saving...' : 'Save & Continue Later' }}
                    </b-button>
                    <b-button @click="updateCase" variant="success">
                        Save Case
                    </b-button>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { CaseDetail, CaseNote, CaseWatcher } from '@/models/Case';
import { PatientDocument } from '@/models/PatientDocument';
import { Prescription } from '@/models/Prescription';
import { Store } from '@/models/Store';
import { Prescriber } from '@/models/Prescriber';
import SearchComponent from '@/components/SearchComponent.vue';
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import { Table, TableColumn } from 'element-ui';
// @ts-ignore
import { Radio } from '@/components/index';
import CasesWatcherComponent from '@/components/Common/CasesWatcherComponent.vue';
import CaseAssignmentComponent from '@/components/Common/CaseAssignmentComponent.vue';
import NotesList from '@/components/Note/NotesList.vue';
import axios, { AxiosError } from 'axios';
import { EntityType } from "@/models/EntityType";
import { BvModalEvent, BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import { Shipment } from "@/models/Shipment";
import moment from "moment";
import { Lock, LockHandler } from '@/mixins/LockHandler';
import TaskQuestions from '../Tasks/TaskQuestions.vue';
import { UserTaskAnswer, UserTaskQuestion } from '@/models/UserTask';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import NotesForm from '../Note/NotesForm.vue';
@Component({
    name: "CaseDetailComponent",
    components: {
        HistoryModal,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Radio.name]: Radio,
        NotesList,
        NotesForm,
        TaskQuestions,
        CasesWatcherComponent,
        CaseAssignmentComponent,
        SearchComponent
    }
})
export default class CaseDetailComponent extends Mixins(LockHandler) {
    EntityType = EntityType;

    @Prop({ default: null }) private caseSelected!: CaseDetail | null;
    @Prop() private show?: string | null;

    @Ref("questions") protected questionComponent?: TaskQuestions;

    private prescriptions: Prescription[] = [];
    private isResolved = 'false';
    private carePointsError = 'false';
    private option = 'accept';
    private newNote = '';
    private editingType = false;
    private editingCategory = false;
    private editingStore = false;
    private editingDoctor = false;
    private selectedProblem = '';
    private selectedCategory: string | null = null;
    private selectedStore: Store | null = null;
    private selectedDoctor: Prescriber | null = null;
    private localCaseAssignment: any = null;

    protected questions: UserTaskQuestion[] = [];
    protected answers: UserTaskAnswer[] = [];
    protected showErrors = false;
    private savingPartialAnswers = false;

    private caseDocuments: PatientDocument[] = [];
    private showDocUpload = false;
    private newDocFile: File | null = null;
    private newDocFileName = '';
    private isUploadingDoc = false;

    private caseType = EntityType.Case;

    @Ref("notesList") protected notesList!: NotesList;

    created() {
        this.getCaseProblems();
        this.getCaseCategories();
        this.getCaseDetail();
    }

    mounted() {
    }

    get mode() {
        if (this.show) return this.show;

        if (this.caseSelected?.accepted) return "completed";
        if (this.caseSelected?.resolved) return "resolved";
        return "opened";
    }

    get isEditable(): boolean {
        // Only completed cases should be non-editable
        // Opened and resolved cases can be edited
        return this.mode !== 'completed';
    }

    get hasLinkedPrescriptions(): boolean {
        return this.prescriptions.length > 0;
    }

    get doctorDisplayName(): string {
        if (this.selectedDoctor) {
            const name = `${this.selectedDoctor.lastName}, ${this.selectedDoctor.firstName}`;
            return this.selectedDoctor.npiNumber ? `${name} (NPI: ${this.selectedDoctor.npiNumber})` : name;
        }
        if (this.caseSelected?.prescriberLastName) {
            const name = `${this.caseSelected.prescriberLastName}, ${this.caseSelected.prescriberFirstName || ''}`;
            return this.caseSelected.prescriberNPI ? `${name} (NPI: ${this.caseSelected.prescriberNPI})` : name;
        }
        return '(None)';
    }

    private documentFields: BvTableFieldArray = [
        { key: 'fileName', label: 'File Name' },
        { key: 'created', label: 'Uploaded' },
        { key: 'actions', label: '', class: 'text-right' }
    ];

    private shipmentFields: BvTableFieldArray = [
        {
            key: "shipped",
            formatter: (d: any) => moment(d).format("L"),
        },
        { key: "shipmentType" },
        {
            key: "trackingNumber",
            label: "Tracking",
        },
    ];

    async shipmentTableProvider(ctx: BvTableCtxObject): Promise<Shipment[]> {
        const uri = ctx.apiUrl!;
        try {
            const resp = await axios.get<Shipment[]>(uri);
            return resp.data.map(s => new Shipment(s));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            return [];
        }
    }

    protected problems: string[] = [];
    protected categories: any[] = [];

    get categoryOptions() {
        return [
            { text: "(None)", value: null },
            ...this.categories.map(c => ({ text: c.name, value: c.name }))
        ];
    }

    async getCaseProblems() {
        try {
            const resp = await axios.get<string[]>("/Case/problems");
            this.problems = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async getCaseCategories() {
        try {
            const resp = await axios.get<any[]>("/Case/categories");
            this.categories = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    protected caseLock: Lock | null = null;
    private lockingInProgress = false;
    private isModalVisible = false;

    get disableFields(): boolean {
        // Allow component to render while lock is being acquired
        if (this.lockingInProgress) return true;
        // If we have a case but no lock yet, allow viewing (disable editing)
        if (this.caseSelected?.id && !this.caseLock) return true;
        // Normal locking logic
        return !this.caseLock;
    }

    @Watch('caseSelected')
    async getCaseDetail() {
        try {
            if (!this.caseSelected?.id) {
                this.caseLock = null;
                this.lockingInProgress = false;
                this.questions = [];
                this.answers = [];
                this.isModalVisible = false;
                return;
            }

            this.isModalVisible = true;

        await this.$nextTick();

        if (this.$refs.caseDetailModal) {
            (this.$refs.caseDetailModal as any).show();
        }

        try {
            this.lockingInProgress = true;
            this.caseLock = await this.addLockWithURL(`/Case/${this.caseSelected.id}/lock`, 60000);
            this.lockingInProgress = false;
        } catch (err) {
            this.lockingInProgress = false;
            const error = err as AxiosError;
            if (error.response && error.response.status == 418) {
                // case locked
                this.showLockedMessage(
                    EntityType.Case,
                    this.caseSelected?.id.toString(),
                    this.caseSelected?.id.toString(),
                    error.response.data,
                );
                this.$emit("hidden");
                return;
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }

        try {
            const resp = await axios.get(`/Case/questions/${this.caseSelected.id}`);
            this.questions = resp.data.questions;
            this.answers = resp.data.answers;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

        this.carePointsError = this.caseSelected.error ? 'true' : 'false';
        this.isResolved = 'false';
        this.newNote = '';
        this.option = 'reject';
        this.editingType = false;
        this.editingCategory = false;
        this.editingStore = false;
        this.editingDoctor = false;
        this.selectedProblem = this.caseSelected.problem;
        this.selectedCategory = this.caseSelected.categoryName || null;
        this.localCaseAssignment = this.caseSelected.assignment || null;

        // Load store if case has storeId
        this.selectedStore = null;
        if (this.caseSelected.storeId) {
            try {
                const storeResp = await axios.get(`/Store/${this.caseSelected.storeId}`);
                this.selectedStore = storeResp.data;
            } catch (err) {
                console.warn('Failed to load store:', err);
            }
        }

        // Load prescriber if case has prescriberId
        this.selectedDoctor = null;
        if (this.caseSelected.prescriberId) {
            try {
                const prescriberResp = await axios.get(`/Prescriber/${this.caseSelected.prescriberId}`);
                this.selectedDoctor = prescriberResp.data;
            } catch (err) {
                console.warn('Failed to load prescriber:', err);
            }
        }

        try {
            const response = await axios.get(`/Case/${this.caseSelected.id}/Prescriptions`);
            this.prescriptions = response.data;
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        }

        await this.loadCaseDocuments();
        } catch (error) {
            this.$notification(NotificationOptions.error('Error in getCaseDetail: ' + error));
        }
    }

    async handleSavePartialAnswers() {
        try {
            this.savingPartialAnswers = true;

            const newCaseNote: CaseNote | any = {
                id: this.caseSelected!.id,
                patientId: this.caseSelected!.patientId,
                problem: this.selectedProblem,
                categoryName: this.selectedCategory,
                note: this.newNote,
                error: this.carePointsError == 'true',
                watchers: this.caseSelected!.watchers || [],
                assignment: this.localCaseAssignment,
                storeId: this.selectedStore?.id ?? this.caseSelected!.storeId,
                prescriberId: this.selectedDoctor?.id ?? null,
                // Keep case in current status - don't mark as resolved
                resolvedBy: null,
                acceptedBy: null
            };

            // Save without validation
            await axios.post('/Case/', {
                caseProblem: newCaseNote,
                prescriptions: [],
                answers: this.answers,
            });

            this.newNote = '';
            this.$emit('updateCase');

            this.$notification(NotificationOptions.success("Partial answers saved."));

            this.$bvModal.hide("case-detail");
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.savingPartialAnswers = false;
        }
    }

    async updateCase() {
        if (!this.isEditable) return;

        try {
            const newCaseNote: CaseNote | any = {
                id: this.caseSelected!.id,
                patientId: this.caseSelected!.patientId,
                problem: this.selectedProblem,
                categoryName: this.selectedCategory,
                note: this.newNote,
                error: this.carePointsError == 'true',
                watchers: this.caseSelected!.watchers || [],
                assignment: this.localCaseAssignment,
                storeId: this.selectedStore?.id ?? this.caseSelected!.storeId,
                prescriberId: this.selectedDoctor?.id ?? null
            };

            if (this.mode == 'opened') {
                newCaseNote.resolvedBy = this.isResolved;
                newCaseNote.acceptedBy = 'false';

                // Validate required questions have answers on ALL saves (not just when resolving)
                if (this.questionComponent?.answersHaveErrors()) {
                    this.showErrors = true;
                    this.$notification(NotificationOptions.error("Please answer all required questions before saving."));
                    return;
                }
            } else {
                newCaseNote.resolvedBy = this.option == 'accept' ? 'true' : 'false';
                newCaseNote.acceptedBy = this.option == 'accept' ? 'true' : 'false';
            }

            this.caseSelected!.error = newCaseNote.error;
            this.caseSelected!.resolved = newCaseNote.resolved;
            await axios.post('/Case/', {
                caseProblem: newCaseNote,
                prescriptions: [],
                answers: this.answers,
            });

            this.newNote = '';
            this.isResolved = 'false';
            this.$emit('updateCase');

            this.$notification(NotificationOptions.success("Case updated"));

            this.$bvModal.hide("case-detail");
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    get modalTitle() {
        if (this.caseSelected?.id) return `Case Details (ID: ${this.caseSelected.id})`;
        return 'Case Details';
    }

    async hideModal() {
        this.$emit('hidden');
        if (this.caseLock?.refreshURL) {
            this.releaseLockAtURL(this.caseLock.refreshURL);
        }
    }

    refreshNotesList() {
        this.notesList.loadNotes();
    }

    async loadCaseDocuments() {
        if (!this.caseSelected?.id) {
            this.caseDocuments = [];
            return;
        }
        try {
            const resp = await axios.get<PatientDocument[]>(`/Case/${this.caseSelected.id}/documents`);
            this.caseDocuments = resp.data;
        } catch (err) {
            console.warn('Failed to load case documents:', err);
            this.caseDocuments = [];
        }
    }

    async uploadDocument() {
        if (!this.caseSelected?.id || !this.newDocFile || !this.newDocFileName) return;

        try {
            this.isUploadingDoc = true;
            const formData = new FormData();
            formData.append('document', this.newDocFile);
            formData.append('fileName', this.newDocFileName);

            await axios.post(`/Case/${this.caseSelected.id}/documents/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            this.newDocFile = null;
            this.newDocFileName = '';
            this.showDocUpload = false;
            await this.loadCaseDocuments();
            this.$notification(NotificationOptions.success("Document uploaded"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isUploadingDoc = false;
        }
    }

    async viewDocument(doc: PatientDocument) {
        try {
            const resp = await axios.get(`/Patient/${doc.patientId}/document/${doc.imageId}`, {
                responseType: 'blob'
            });
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async unlinkDocument(doc: PatientDocument) {
        if (!this.caseSelected?.id || !doc.id) return;
        try {
            await axios.delete(`/Case/${this.caseSelected.id}/documents/${doc.id}`);
            await this.loadCaseDocuments();
            this.$notification(NotificationOptions.success("Document removed from case"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    onWatchersChange(watchers: CaseWatcher[]) {
        if (this.caseSelected) {
            this.caseSelected.watchers = watchers;
        }
    }
}
</script>
